import React, { useState, useEffect, useRef } from 'react';
import { ChefHat, LocationEdit as Edit, Trash2, MapPin, Upload, User, Clock, Package, Save, X, TriangleAlert as AlertTriangle, CircleCheck as CheckCircle, Loader, UtensilsCrossed, RefreshCw, CirclePlus as PlusCircle, LayoutDashboard, Power, PackagePlus } from 'lucide-react';
import { trackAccountManagement, trackError } from '../Utils/analytics';
import { getCurrentLocationDetails, getCountriesList } from '../Utils/location';
import { INVENTORY_UPDATE_URL, GET_FOOD_ITEMS_URL, MY_KITCHEN_URL, CREATE_KITCHEN_URL, UPDATE_KITCHEN_URL, DELETE_KITCHEN_URL, KITCHEN_STATUS_URL, DELETE_FOOD_URL, RESTOCK_FOOD_URL, TOGGLE_FOOD_STATUS_URL } from '../../config/api';
import { authenticatedFetch, getFirstImageUrl, getAllImageUrls, FOOD_FALLBACK_IMG } from '../Utils/apiHelpers';
import SellFoodModal from '../SellFoodModal/SellFoodModal';
import EditFoodModal from '../EditFoodModal/EditFoodModal';
import KitchenDashboardTab from './KitchenDashboardTab';
import './UserKitchenManager.css';

interface Kitchen {
  kitchen_id: number;
  kitchen_name: string;
  kitchen_type: string;
  contact_number: string;
  kitchen_address: string;
  state: string;
  city: string;
  country: string;
  pincode: string;
  kitchen_category: string;
  is_active: boolean;
  kitchen_photo?: string;
  user_id: number;
  created_at: string;
  updated_at: string;
  made_by?: string;
  status?: boolean;
  latitude?: number;
  longitude?: number;
  is_approved?: boolean;
  rejection_reason?: string | null;
  created_by?: string;
  contact_email?: string | null;
  delivery_radius_km?: number;
  minimum_order_amount?: number;
  delivery_fee?: number;
  average_preparation_time?: number;
  // Legacy field mappings for backward compatibility
  phone_number?: string;
  address?: string;
  image_url?: string;
  description?: string;
}

interface KitchenFormData {
  kitchen_name: string;
  kitchen_type: string;
  contact_number: string;
  kitchen_address: string;
  state: string;
  city: string;
  country: string;
  pincode: string;
  kitchen_category: string;
  made_by: string;
  latitude: string;
  longitude: string;
  delivery_radius_km: string;
  minimum_order_amount: string;
  delivery_fee: string;
  average_preparation_time: string;
  image: File | null;
}

interface UserKitchenManagerProps {
  showNotification: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

const UserKitchenManager: React.FC<UserKitchenManagerProps> = ({ showNotification }) => {
  const [kitchen, setKitchen] = useState<Kitchen | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'view' | 'edit' | 'create' | 'food_listings' | 'dashboard'>('view');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeletingKitchen, setIsDeletingKitchen] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [locationMessage, setLocationMessage] = useState<{type: 'success' | 'error' | 'notice', text: string} | null>(null);

  const isKitchenBlocked = !!(kitchen && kitchen.is_approved === true && kitchen.status === false);
  const isKitchenRejected = !!(kitchen && kitchen.is_approved === false && kitchen.rejection_reason);

  const handleRecreateKitchen = () => {
    if (!kitchen) return;
    setFormData({
      kitchen_name: kitchen.kitchen_name || '',
      kitchen_type: kitchen.kitchen_type || '',
      contact_number: kitchen.contact_number || kitchen.phone_number || '',
      kitchen_address: kitchen.kitchen_address || kitchen.address || '',
      state: kitchen.state || '',
      city: kitchen.city || '',
      country: kitchen.country || '',
      pincode: kitchen.pincode || '',
      kitchen_category: kitchen.kitchen_category || '',
      made_by: kitchen.made_by || '',
      latitude: kitchen.latitude != null ? String(kitchen.latitude) : '',
      longitude: kitchen.longitude != null ? String(kitchen.longitude) : '',
      delivery_radius_km: kitchen.delivery_radius_km || '15',
      minimum_order_amount: kitchen.minimum_order_amount || '0',
      delivery_fee: kitchen.delivery_fee || '30',
      average_preparation_time: kitchen.average_preparation_time || '30',
      image: null
    });
    if (kitchen.kitchen_photo) {
      setImagePreview(kitchen.kitchen_photo);
    }
    setFormErrors({});
    setActiveTab('create');
    showNotification('info', 'Form Pre-filled', 'Form pre-filled with your previous kitchen details. Please correct the issues and resubmit.');
  };

  const [inventoryEdits, setInventoryEdits] = useState<Record<number, string>>({});
  const [savingFoodId, setSavingFoodId] = useState<number | null>(null);

  const [formData, setFormData] = useState<KitchenFormData>({
    kitchen_name: '',
    kitchen_type: '',
    contact_number: '',
    kitchen_address: '',
    state: '',
    city: '',
    country: '',
    pincode: '',
    kitchen_category: '',
    made_by: '',
    latitude: '',
    longitude: '',
    delivery_radius_km: '15',
    minimum_order_amount: '0',
    delivery_fee: '30',
    average_preparation_time: '30',
    image: null
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Kitchen type options — values match the API exactly
  const kitchenTypes = [
    'Home Kitchen',
    'Commercial Kitchen',
    'Cloud Kitchen',
    'Catering Kitchen'
  ];

  // Kitchen category options — values match the API exactly
  const kitchenCategories = [
    'Vegetarian',
    'Non-Vegetarian',
    'Vegan',
    'Eggetarian',
    'Indian',
    'Chinese',
    'Italian',
    'Mexican',
    'Thai',
    'Mediterranean',
    'American',
    'Fusion',
    'Bakery',
    'Desserts',
    'Healthy',
    'Fast Food',
    'Traditional',
    'International'
  ];

  // Get countries list
  const countries = getCountriesList();

  // Food listings state
  const [foodListings, setFoodListings] = useState<any[]>([]);
  const [foodListingsLoading, setFoodListingsLoading] = useState(false);
  const [foodListingsError, setFoodListingsError] = useState<string | null>(null);
  const [showSellFoodModal, setShowSellFoodModal] = useState(false);
  const [inventorySummary, setInventorySummary] = useState<{
    low_stock_items: number;
    sold_out_items: number;
    total_items: number;
  } | null>(null);

  // Edit / delete / restock state
  const [editingFood, setEditingFood] = useState<any | null>(null);
  const [deletingFoodId, setDeletingFoodId] = useState<number | null>(null);
  const [isDeletingFood, setIsDeletingFood] = useState(false);
  const [restockFoodId, setRestockFoodId] = useState<number | null>(null);
  const [restockQty, setRestockQty] = useState('');
  const [isRestocking, setIsRestocking] = useState(false);
  const [togglingFoodId, setTogglingFoodId] = useState<number | null>(null);

  const fetchFoodListings = async () => {
    setFoodListingsLoading(true);
    setFoodListingsError(null);
    try {
      const response = await authenticatedFetch(GET_FOOD_ITEMS_URL, {
        method: 'GET',
        headers: { 'accept': 'application/json' },
      });
      if (!response.ok) {
        if (response.status === 401) {
          setFoodListingsError('Session expired. Please log in again.');
        } else if (response.status === 404) {
          setFoodListings([]);
        } else {
          setFoodListingsError('Failed to load food listings. Please try again.');
        }
        return;
      }
      const data = await response.json();
      const foods: any[] = Array.isArray(data) ? data : [];
      setFoodListings(foods);
      setInventorySummary({
        low_stock_items: foods.filter((f: any) => f.available_quantity <= (f.low_stock_threshold ?? 5) && f.available_quantity > 0).length,
        sold_out_items: foods.filter((f: any) => f.available_quantity === 0).length,
        total_items: foods.length,
      });
      const edits: Record<number, string> = {};
      foods.forEach((item: any) => {
        edits[item.food_id] = String(item.available_quantity ?? 0);
      });
      setInventoryEdits(edits);
    } catch {
      setFoodListingsError('Network error. Please check your connection.');
    } finally {
      setFoodListingsLoading(false);
    }
  };

  const handleSaveQuantity = async (foodId: number) => {
    const rawValue = inventoryEdits[foodId];
    const qty = parseInt(rawValue, 10);
    if (isNaN(qty) || qty < 0) {
      showNotification('error', 'Invalid Quantity', 'Please enter a valid non-negative number.');
      return;
    }
    setSavingFoodId(foodId);
    try {
      const response = await authenticatedFetch(INVENTORY_UPDATE_URL(foodId), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json',
        },
        body: JSON.stringify({ available_quantity: qty }),
      });
      if (response.ok) {
        setFoodListings(prev =>
          prev.map(item =>
            item.food_id === foodId ? { ...item, available_quantity: qty } : item
          )
        );
        showNotification('success', 'Quantity Updated', `Stock updated to ${qty}.`);
      } else {
        const err = await response.json().catch(() => ({}));
        showNotification('error', 'Update Failed', err.message || 'Failed to update quantity.');
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection and try again.');
    } finally {
      setSavingFoodId(null);
    }
  };

  const handleDeleteFood = async (foodId: number) => {
    setIsDeletingFood(true);
    try {
      const res = await authenticatedFetch(DELETE_FOOD_URL(foodId), { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setFoodListings(prev => prev.filter(f => f.food_id !== foodId));
        showNotification('success', 'Food Deleted', 'Food item has been removed.');
      } else {
        showNotification('error', 'Delete Failed', data.message || 'Could not delete food item.');
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection.');
    } finally {
      setIsDeletingFood(false);
      setDeletingFoodId(null);
    }
  };

  const handleRestock = async (foodId: number) => {
    const qty = parseInt(restockQty, 10);
    if (isNaN(qty) || qty <= 0) {
      showNotification('error', 'Invalid Quantity', 'Please enter a positive number of units to add.');
      return;
    }
    setIsRestocking(true);
    try {
      const res = await authenticatedFetch(RESTOCK_FOOD_URL(foodId, qty), { method: 'PATCH' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setFoodListings(prev =>
          prev.map(f => f.food_id === foodId ? { ...f, available_quantity: data.available_quantity } : f)
        );
        showNotification('success', 'Restocked', `${data.food_name} now has ${data.available_quantity} units.`);
        setRestockFoodId(null);
        setRestockQty('');
      } else {
        showNotification('error', 'Restock Failed', data.message || 'Could not restock.');
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection.');
    } finally {
      setIsRestocking(false);
    }
  };

  const handleToggleStatus = async (foodId: number) => {
    setTogglingFoodId(foodId);
    try {
      const res = await authenticatedFetch(TOGGLE_FOOD_STATUS_URL(foodId), { method: 'PATCH' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setFoodListings(prev =>
          prev.map(f => f.food_id === foodId ? { ...f, is_available: data.is_available } : f)
        );
        showNotification('success', 'Status Updated', data.message || 'Availability toggled.');
        window.dispatchEvent(new CustomEvent('foodAvailabilityChanged'));
      } else {
        showNotification('error', 'Toggle Failed', data.message || 'Could not update status.');
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection.');
    } finally {
      setTogglingFoodId(null);
    }
  };

  // Store kitchenId in localStorage and dispatch event when kitchen data changes
  useEffect(() => {
    if (kitchen?.kitchen_id) {
      localStorage.setItem('kitchenId', kitchen.kitchen_id.toString());

      // Dispatch kitchen created/updated event
      window.dispatchEvent(new CustomEvent('kitchenCreated', {
        detail: { kitchenId: kitchen.kitchen_id }
      }));
    } else {
      localStorage.removeItem('kitchenId');
    }
  }, [kitchen]);

  const prevApprovalRef = useRef<{ is_approved: boolean | null; status: boolean | null }>({ is_approved: null, status: null });

  const fetchKitchen = async (silent: boolean = false) => {
    if (!silent) setIsLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(MY_KITCHEN_URL, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const kitchenData: Kitchen = await response.json();

        const mappedKitchen: Kitchen = {
          ...kitchenData,
          is_active: kitchenData.is_active ?? kitchenData.status ?? false,
          kitchen_type: kitchenData.kitchen_type || '',
          contact_number: kitchenData.contact_number || kitchenData.phone_number || '',
          kitchen_address: kitchenData.kitchen_address || kitchenData.address || '',
          state: kitchenData.state || '',
          city: kitchenData.city || '',
          country: kitchenData.country || '',
          pincode: kitchenData.pincode || '',
          kitchen_category: kitchenData.kitchen_category || '',
          kitchen_photo: kitchenData.kitchen_photo || kitchenData.image_url || ''
        };

        const prev = prevApprovalRef.current;
        const newApproved = mappedKitchen.is_approved ?? false;
        const newStatus = mappedKitchen.status ?? true;

        if (prev.is_approved !== null && prev.status !== null) {
          if (prev.is_approved === false && newApproved === true) {
            showNotification('success', 'Kitchen Approved', 'Your kitchen has been approved! You can now list food items.');
            window.dispatchEvent(new CustomEvent('kitchenApprovalChanged', { detail: { kitchenId: mappedKitchen.kitchen_id, approved: true } }));
          } else if (prev.is_approved === true && newApproved === false) {
            showNotification('error', 'Kitchen Rejected', mappedKitchen.rejection_reason ? `Your kitchen was rejected: ${mappedKitchen.rejection_reason}. Please recreate and resubmit.` : 'Your kitchen was rejected. Please recreate and resubmit.');
            window.dispatchEvent(new CustomEvent('kitchenApprovalChanged', { detail: { kitchenId: mappedKitchen.kitchen_id, approved: false } }));
          }
          if (prev.status === true && newStatus === false) {
            showNotification('warning', 'Kitchen Blocked', 'Your kitchen has been blocked by the administrator. You cannot modify your kitchen or accept new orders until it is unblocked.');
            window.dispatchEvent(new CustomEvent('kitchenApprovalChanged', { detail: { kitchenId: mappedKitchen.kitchen_id, blocked: true } }));
          } else if (prev.status === false && newStatus === true) {
            showNotification('success', 'Kitchen Unblocked', 'Your kitchen has been unblocked. You can now resume operations.');
            window.dispatchEvent(new CustomEvent('kitchenApprovalChanged', { detail: { kitchenId: mappedKitchen.kitchen_id, blocked: false } }));
          }
        }

        prevApprovalRef.current = { is_approved: newApproved, status: newStatus };

        setKitchen(mappedKitchen);

        localStorage.setItem('kitchenId', mappedKitchen.kitchen_id.toString());

        window.dispatchEvent(new CustomEvent('kitchenCreated', {
          detail: { kitchenId: mappedKitchen.kitchen_id }
        }));

        if (!silent) setActiveTab('view');
      } else if (response.status === 404) {
        setKitchen(null);
        localStorage.removeItem('kitchenId');
        if (!silent) setActiveTab('create');
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch kitchen information.');
        trackError('kitchen_fetch_failed', 'kitchen_manager');
      }
    } catch (error) {
      console.error('Kitchen fetch error:', error);
      setError('Network error. Please check your connection and try again.');
      trackError('kitchen_fetch_network_error', 'kitchen_manager');
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const handleDetectLocation = async () => {
    setIsDetectingLocation(true);
    setLocationMessage(null);

    try {
      const result = await getCurrentLocationDetails();

      if ('type' in result) {
        setLocationMessage({
          type: 'error',
          text: result.message
        });
      } else {
        setFormData(prev => ({
          ...prev,
          city: result.city || prev.city,
          state: result.state || prev.state,
          country: result.country_name || prev.country,
          pincode: result.postal_code || prev.pincode,
          kitchen_address: result.full_address || prev.kitchen_address,
          latitude: result.latitude != null ? String(result.latitude) : prev.latitude,
          longitude: result.longitude != null ? String(result.longitude) : prev.longitude,
        }));

        setLocationMessage({
          type: 'success',
          text: `Location detected: ${result.city}, ${result.state}, ${result.country_name}`
        });

        const fieldsToCheck = ['city', 'state', 'country', 'pincode', 'kitchen_address', 'latitude', 'longitude'];
        setFormErrors(prev => {
          const newErrors = { ...prev };
          fieldsToCheck.forEach(f => delete newErrors[f]);
          return newErrors;
        });
      }
    } catch {
      setLocationMessage({
        type: 'error',
        text: 'Failed to detect location. Please enter manually.'
      });
    } finally {
      setIsDetectingLocation(false);
    }
  };

  const handleInputChange = (field: keyof KitchenFormData, value: string | File | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleImageUpload = (file: File) => {
    setFormData(prev => ({
      ...prev,
      image: file
    }));
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    
    // Clear image error
    if (formErrors.image) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.image;
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.kitchen_name.trim()) newErrors.kitchen_name = 'Kitchen name is required';
    if (!formData.kitchen_address.trim()) newErrors.kitchen_address = 'Kitchen address is required';
    if (!formData.contact_number.trim()) newErrors.contact_number = 'Contact number is required';
    if (!formData.kitchen_type.trim()) newErrors.kitchen_type = 'Kitchen type is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    if (!formData.pincode.trim()) newErrors.pincode = 'Pincode is required';
    if (!formData.kitchen_category.trim()) newErrors.kitchen_category = 'Kitchen category is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    if (!formData.pincode.trim()) newErrors.pincode = 'Pincode is required';
    if (!formData.kitchen_category.trim()) newErrors.kitchen_category = 'Kitchen category is required';
    if (!formData.made_by.trim()) newErrors.made_by = 'Chef/Owner name is required';

    // Kitchen name length validation
    if (formData.kitchen_name && formData.kitchen_name.length > 300) {
      newErrors.kitchen_name = 'Kitchen name must be less than 300 characters';
    }

    // Kitchen address length validation
    if (formData.kitchen_address && formData.kitchen_address.length > 500) {
      newErrors.kitchen_address = 'Kitchen address must be less than 500 characters';
    }

    // Contact number validation (10 digits)
    if (formData.contact_number) {
      const cleanPhone = formData.contact_number.replace(/\s/g, '');
      if (!/^[0-9]{10}$/.test(cleanPhone)) {
        newErrors.contact_number = 'Please enter a valid 10-digit phone number';
      }
    }

    // Latitude validation (optional, must be a valid decimal if provided)
    if (formData.latitude.trim()) {
      const lat = parseFloat(formData.latitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        newErrors.latitude = 'Latitude must be a number between -90 and 90';
      }
    }

    // Longitude validation (optional, must be a valid decimal if provided)
    if (formData.longitude.trim()) {
      const lng = parseFloat(formData.longitude);
      if (isNaN(lng) || lng < -180 || lng > 180) {
        newErrors.longitude = 'Longitude must be a number between -180 and 180';
      }
    }

    // Kitchen photo required on create
    if (!kitchen && !formData.image) {
      newErrors.image = 'Kitchen photo is required';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateKitchen = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      trackError('kitchen_creation_validation_failed', 'kitchen_manager');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        setError('Authentication required. Please log in again.');
        setIsSubmitting(false);
        return;
      }

      const submitData = new FormData();
      submitData.append('kitchen_name', formData.kitchen_name.trim());
      submitData.append('kitchen_type', formData.kitchen_type);
      submitData.append('contact_number', formData.contact_number.replace(/\s/g, ''));
      submitData.append('kitchen_address', formData.kitchen_address.trim());
      submitData.append('state', formData.state.trim());
      submitData.append('city', formData.city.trim());
      submitData.append('country', formData.country.trim());
      submitData.append('pincode', formData.pincode.trim());
      submitData.append('kitchen_category', formData.kitchen_category);
      submitData.append('made_by', formData.made_by.trim());

      if (formData.latitude.trim()) {
        submitData.append('latitude', formData.latitude.trim());
      }
      if (formData.longitude.trim()) {
        submitData.append('longitude', formData.longitude.trim());
      }
      if (formData.delivery_radius_km.trim()) {
        submitData.append('delivery_radius_km', formData.delivery_radius_km.trim());
      }
      if (formData.minimum_order_amount.trim()) {
        submitData.append('minimum_order_amount', formData.minimum_order_amount.trim());
      }
      if (formData.delivery_fee.trim()) {
        submitData.append('delivery_fee', formData.delivery_fee.trim());
      }
      if (formData.average_preparation_time.trim()) {
        submitData.append('average_preparation_time', formData.average_preparation_time.trim());
      }

      if (formData.image) {
        submitData.append('kitchen_photo', formData.image);
      }

      const response = await authenticatedFetch(CREATE_KITCHEN_URL, {
        method: 'POST',
        body: submitData,
      });

      const data = await response.json();

      if (response.ok) {
        setKitchen(data);

        // Store kitchenId in localStorage immediately
        localStorage.setItem('kitchenId', data.kitchen_id.toString());

        // Dispatch kitchen created event
        window.dispatchEvent(new CustomEvent('kitchenCreated', {
          detail: { kitchenId: data.kitchen_id }
        }));

        setActiveTab('view');
        showNotification('success', 'Kitchen Created', 'Your kitchen has been submitted for admin approval. It will be publicly visible once approved.');
        trackAccountManagement('kitchen_created');
        
        // Reset form
        setFormData({
          kitchen_name: '',
          kitchen_type: '',
          contact_number: '',
          kitchen_address: '',
          state: '',
          city: '',
          country: '',
          pincode: '',
          kitchen_category: '',
          made_by: '',
          latitude: '',
          longitude: '',
          delivery_radius_km: '15',
          minimum_order_amount: '0',
          delivery_fee: '30',
          average_preparation_time: '30',
          image: null
        });
        setImagePreview(null);
      } else {
        let errorMessage = 'Failed to create kitchen';
        
        switch (response.status) {
          case 400:
            errorMessage = data.detail || data.message || 'Invalid data provided. Please check all fields.';
            break;
          case 409:
            errorMessage = 'You already have a kitchen. Only one kitchen per user is allowed.';
            break;
          case 422:
            errorMessage = data.message || 'Invalid or missing required fields. Please check all fields and try again.';
            console.error('422 Validation Error Details:', data);
            break;
          case 415:
            errorMessage = 'Invalid image format. Please use JPG, PNG, or WebP.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = data.message || `Error ${response.status}: Please try again.`;
        }
        
        setError(errorMessage);
        trackError('kitchen_creation_api_failed', 'kitchen_manager');
      }
    } catch (error) {
      console.error('Kitchen creation error:', error);
      setError('Network error. Please check your connection and try again.');
      trackError('kitchen_creation_network_error', 'kitchen_manager');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateKitchen = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!kitchen || !validateForm()) {
      trackError('kitchen_update_validation_failed', 'kitchen_manager');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const submitData = new FormData();
      submitData.append('kitchen_name', formData.kitchen_name.trim());
      submitData.append('kitchen_type', formData.kitchen_type);
      submitData.append('contact_number', formData.contact_number.replace(/\s/g, ''));
      submitData.append('kitchen_address', formData.kitchen_address.trim());
      submitData.append('state', formData.state.trim());
      submitData.append('city', formData.city.trim());
      submitData.append('country', formData.country.trim());
      submitData.append('pincode', formData.pincode.trim());
      submitData.append('kitchen_category', formData.kitchen_category);
      submitData.append('made_by', formData.made_by.trim());

      if (formData.latitude.trim()) {
        submitData.append('latitude', formData.latitude.trim());
      }
      if (formData.longitude.trim()) {
        submitData.append('longitude', formData.longitude.trim());
      }
      if (formData.delivery_radius_km.trim()) {
        submitData.append('delivery_radius_km', formData.delivery_radius_km.trim());
      }
      if (formData.minimum_order_amount.trim()) {
        submitData.append('minimum_order_amount', formData.minimum_order_amount.trim());
      }
      if (formData.delivery_fee.trim()) {
        submitData.append('delivery_fee', formData.delivery_fee.trim());
      }
      if (formData.average_preparation_time.trim()) {
        submitData.append('average_preparation_time', formData.average_preparation_time.trim());
      }

      if (formData.image) {
        submitData.append('kitchen_photo', formData.image);
      }

      const response = await authenticatedFetch(UPDATE_KITCHEN_URL(kitchen.kitchen_id), {
        method: 'PUT',
        body: submitData,
      });

      const data = await response.json();

      if (response.ok) {
        setKitchen(data);

        // Ensure kitchenId remains in localStorage
        localStorage.setItem('kitchenId', data.kitchen_id.toString());

        setActiveTab('view');
        showNotification('success', 'Kitchen Updated', 'Your kitchen information has been updated successfully!');
        trackAccountManagement('kitchen_updated');
      } else {
        let errorMessage = 'Failed to update kitchen';
        
        switch (response.status) {
          case 400:
            errorMessage = data.detail || data.message || 'Invalid data provided. Please check all fields.';
            break;
          case 404:
            errorMessage = 'Kitchen not found. Please refresh the page.';
            break;
          case 415:
            errorMessage = 'Invalid image format. Please use JPG, PNG, or WebP.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = data.message || `Error ${response.status}: Please try again.`;
        }
        
        setError(errorMessage);
        trackError('kitchen_update_api_failed', 'kitchen_manager');
      }
    } catch (error) {
      console.error('Kitchen update error:', error);
      setError('Network error. Please check your connection and try again.');
      trackError('kitchen_update_network_error', 'kitchen_manager');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteKitchen = async () => {
    if (!kitchen) return;

    setIsDeletingKitchen(true);
    setError(null);

    try {
      const response = await authenticatedFetch(DELETE_KITCHEN_URL(kitchen.kitchen_id), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        setKitchen(null);
        
        // Remove kitchenId from localStorage
        localStorage.removeItem('kitchenId');
        
        // Dispatch kitchen deleted event
        window.dispatchEvent(new CustomEvent('kitchenDeleted'));
        
        setShowDeleteConfirmation(false);
        setActiveTab('create');
        showNotification('success', 'Kitchen Deleted', 'Your kitchen has been deleted successfully.');
        trackAccountManagement('kitchen_deleted');
      } else {
        const errorData = await response.json();
        if (response.status === 404) {
          setError('Kitchen not found or already deleted.');
        } else if (response.status === 500) {
          setError('Server error occurred while deleting kitchen. Please try again later.');
        } else {
          setError(errorData.message || 'Failed to delete kitchen. Please try again.');
        }
        trackError('kitchen_deletion_failed', 'kitchen_manager');
      }
    } catch (error) {
      console.error('Kitchen deletion error:', error);
      setError('Network error occurred while deleting kitchen. Please check your connection.');
      trackError('kitchen_deletion_network_error', 'kitchen_manager');
    } finally {
      setIsDeletingKitchen(false);
    }
  };

  const handleToggleKitchenStatus = async () => {
    if (!kitchen) return;

    setIsUpdatingStatus(true);
    setError(null);

    try {
      const newStatus = !kitchen.is_active;

      const response = await authenticatedFetch(KITCHEN_STATUS_URL(kitchen.kitchen_id, newStatus), {
        method: 'POST',
        headers: { 'accept': 'application/json' },
      });

      if (response.ok) {
        await response.json();

        const updatedKitchen = { ...kitchen, is_active: newStatus };
        setKitchen(updatedKitchen);

        localStorage.setItem('kitchenId', updatedKitchen.kitchen_id.toString());

        window.dispatchEvent(new CustomEvent('kitchenStatusChanged', {
          detail: { kitchenId: kitchen.kitchen_id, isActive: newStatus }
        }));

        showNotification(
          'success',
          'Status Updated',
          `Kitchen is now ${newStatus ? 'online and accepting orders' : 'offline'}`
        );
        trackAccountManagement('kitchen_updated');
      } else {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 404) {
          setError('Kitchen not found. Please refresh the page.');
        } else if (response.status === 422) {
          setError('Invalid status value. Please try again.');
        } else {
          setError(errorData.message || 'Failed to update kitchen status.');
        }

        trackError('kitchen_status_update_failed', 'kitchen_manager');
      }
    } catch (error) {
      console.error('Kitchen status update error:', error);
      setError('Network error. Please check your connection and try again.');
      trackError('kitchen_status_update_network_error', 'kitchen_manager');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleEditClick = () => {
    if (kitchen) {
      setFormData({
        kitchen_name: kitchen.kitchen_name || '',
        kitchen_type: kitchen.kitchen_type || '',
        contact_number: kitchen.contact_number || '',
        kitchen_address: kitchen.kitchen_address || '',
        state: kitchen.state || '',
        city: kitchen.city || '',
        country: kitchen.country || '',
        pincode: kitchen.pincode || '',
        kitchen_category: kitchen.kitchen_category || '',
        made_by: kitchen.made_by || '',
        latitude: kitchen.latitude != null ? String(kitchen.latitude) : '',
        longitude: kitchen.longitude != null ? String(kitchen.longitude) : '',
        delivery_radius_km: kitchen.delivery_radius_km != null ? String(kitchen.delivery_radius_km) : '15',
        minimum_order_amount: kitchen.minimum_order_amount != null ? String(kitchen.minimum_order_amount) : '0',
        delivery_fee: kitchen.delivery_fee != null ? String(kitchen.delivery_fee) : '30',
        average_preparation_time: kitchen.average_preparation_time != null ? String(kitchen.average_preparation_time) : '30',
        image: null
      });
      setImagePreview(kitchen.kitchen_photo || null);
      setActiveTab('edit');
    }
  };

  const handleCancelEdit = () => {
    setActiveTab('view');
    setFormData({
      kitchen_name: '',
      kitchen_type: '',
      contact_number: '',
      kitchen_address: '',
      state: '',
      city: '',
      country: '',
      pincode: '',
      kitchen_category: '',
      made_by: '',
      latitude: '',
      longitude: '',
      image: null
    });
    setImagePreview(null);
    setFormErrors({});
    setError(null);
    setLocationMessage(null);
  };

  useEffect(() => {
    fetchKitchen();
  }, []);

  useEffect(() => {
    const handleFocus = () => fetchKitchen(true);
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  useEffect(() => {
    if (activeTab === 'food_listings') {
      fetchFoodListings();
    }
  }, [activeTab]);

  useEffect(() => {
    const handleFoodCreated = () => {
      if (activeTab === 'food_listings') fetchFoodListings();
    };
    window.addEventListener('foodItemCreated', handleFoodCreated);
    return () => window.removeEventListener('foodItemCreated', handleFoodCreated);
  }, [activeTab]);

  if (isLoading) {
    return (
      <div className="user-profile-empty-state">
        <div className="user-profile-loading-spinner" style={{ width: '40px', height: '40px', marginBottom: '1rem' }}></div>
        <p>Loading your kitchen...</p>
      </div>
    );
  }

  return (
    <div className="user-profile-kitchen-display">
      {/* Kitchen Tabs */}
      <div className="kitchen-tabs">
        {kitchen && (
          <button
            className={`kitchen-tab-button ${activeTab === 'view' ? 'active' : ''}`}
            onClick={() => setActiveTab('view')}
          >
            <ChefHat size={16} />
            My Kitchen
          </button>
        )}
        <button
          className={`kitchen-tab-button ${activeTab === 'edit' ? 'active' : ''}`}
          onClick={() => kitchen ? handleEditClick() : setActiveTab('create')}
        >
          <Edit size={16} />
          {kitchen ? 'Edit Kitchen' : 'Create Kitchen'}
        </button>
        {kitchen && (
          <button
            className={`kitchen-tab-button ${activeTab === 'food_listings' ? 'active' : ''}`}
            onClick={() => setActiveTab('food_listings')}
          >
            <UtensilsCrossed size={16} />
            My Food Listings
          </button>
        )}
        {kitchen && (
          <button
            className={`kitchen-tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <LayoutDashboard size={16} />
            Dashboard
          </button>
        )}
      </div>

      {/* View Kitchen */}
      {activeTab === 'view' && kitchen && (
        <div className="user-profile-kitchen-card">
          {isKitchenRejected && (
            <div className="kitchen-pending-banner" style={{ background: '#fef2f2', borderColor: '#dc3545' }}>
              <AlertTriangle size={18} color="#dc3545" />
              <div>
                <strong style={{ color: '#dc3545' }}>Kitchen Rejected</strong>
                <p>Your kitchen was rejected: {kitchen.rejection_reason}</p>
                <button
                  onClick={handleRecreateKitchen}
                  className="user-profile-edit-btn"
                  style={{ background: '#e53e3e', marginTop: '8px' }}
                >
                  <Edit size={16} />
                  Recreate Kitchen
                </button>
              </div>
            </div>
          )}
          {isKitchenBlocked && (
            <div className="kitchen-pending-banner" style={{ background: '#fffbeb', borderColor: '#f59e0b' }}>
              <AlertTriangle size={18} color="#f59e0b" />
              <div>
                <strong style={{ color: '#b45309' }}>Kitchen Blocked</strong>
                <p>Your kitchen has been temporarily blocked by the administrator. You cannot modify your kitchen or accept new orders until it is unblocked.</p>
              </div>
            </div>
          )}
          {kitchen.is_approved === false && !isKitchenRejected && (
            <div className="kitchen-pending-banner">
              <AlertTriangle size={18} />
              <div>
                <strong>Awaiting Admin Approval</strong>
                <p>Your kitchen is under review. It will become publicly visible and appear in food listings once an admin approves it. This usually takes 1–2 business days.</p>
              </div>
            </div>
          )}
          <div className="user-profile-kitchen-image">
            {kitchen.kitchen_photo ? (
              <img src={kitchen.kitchen_photo} alt={kitchen.kitchen_name} />
            ) : (
              <div className="user-profile-kitchen-image-placeholder">
                <ChefHat size={40} />
                <span>No Image</span>
              </div>
            )}
          </div>
          
          <div className="user-profile-kitchen-info">
            <h4>
              {kitchen.kitchen_name}
              <span className={`kitchen-status ${kitchen.is_active ? 'active' : 'inactive'}`}>
                {kitchen.is_active ? 'Online' : 'Offline'}
              </span>
              {kitchen.is_approved === true ? (
                <span className="kitchen-approval-badge approved">Approved</span>
              ) : (
                <span className="kitchen-approval-badge pending">Pending Approval</span>
              )}
            </h4>
            <p><strong>Type:</strong> {kitchen.kitchen_type || 'Not specified'}</p>
            <p><strong>Category:</strong> {kitchen.kitchen_category || 'Not specified'}</p>
            <p><strong>Chef / Owner:</strong> {kitchen.made_by || 'Not specified'}</p>
            <p><strong>Address:</strong> {kitchen.kitchen_address}</p>
            <p><strong>Location:</strong> {[kitchen.city, kitchen.state, kitchen.country].filter(Boolean).join(', ')}</p>
            <p><strong>Pincode:</strong> {kitchen.pincode}</p>
            <p><strong>Phone:</strong> {kitchen.contact_number}</p>
            {kitchen.latitude != null && kitchen.longitude != null && (
              <p><strong>Coordinates:</strong> {kitchen.latitude}, {kitchen.longitude}</p>
            )}
            {kitchen.delivery_fee != null && (
              <p><strong>Delivery Fee:</strong> £{kitchen.delivery_fee}</p>
            )}
            {kitchen.minimum_order_amount != null && (
              <p><strong>Min. Order:</strong> £{kitchen.minimum_order_amount}</p>
            )}
            {kitchen.average_preparation_time != null && (
              <p><strong>Avg. Prep Time:</strong> {kitchen.average_preparation_time} min</p>
            )}
            {kitchen.delivery_radius_km != null && (
              <p><strong>Delivery Radius:</strong> {kitchen.delivery_radius_km} km</p>
            )}
            <p><strong>Created:</strong> {new Date(kitchen.created_at).toLocaleDateString('en-GB')}</p>
            {kitchen.rejection_reason && (
              <p style={{ color: '#e53e3e' }}><strong>Rejection Reason:</strong> {kitchen.rejection_reason}</p>
            )}
            
            <div className="user-profile-edit-actions">
              <button 
                onClick={handleEditClick}
                className="user-profile-edit-btn"
                disabled={isSubmitting || isKitchenBlocked}
                title={isKitchenBlocked ? 'Kitchen is blocked — editing is disabled' : 'Edit kitchen details'}
              >
                <Edit size={16} />
                Edit Kitchen
              </button>
              <button 
                onClick={handleToggleKitchenStatus}
                disabled={isUpdatingStatus || isKitchenBlocked}
                title={isKitchenBlocked ? 'Kitchen is blocked — cannot change online status' : undefined}
                className={`user-profile-edit-btn ${kitchen.is_active ? 'status-active' : 'status-inactive'}`}
              >
                {isUpdatingStatus ? (
                  <>
                    <div className="user-profile-loading-spinner" style={{ width: '16px', height: '16px' }}></div>
                    Updating...
                  </>
                ) : (
                  <>
                    {kitchen.is_active ? <Package size={16} /> : <CheckCircle size={16} />}
                    {kitchen.is_active ? 'Go Offline' : 'Go Online'}
                  </>
                )}
              </button>
              <button 
                onClick={() => setShowDeleteConfirmation(true)}
                className="user-profile-edit-btn"
                style={{ background: '#dc3545' }}
                disabled={isSubmitting}
              >
                <Trash2 size={16} />
                Delete Kitchen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Kitchen Form */}
      {(activeTab === 'create' || activeTab === 'edit') && (
        <div className="user-profile-kitchen-form">
          <div className="user-profile-kitchen-form-header">
            <h4>{kitchen ? 'Edit Kitchen' : 'Create Your Kitchen'}</h4>
            <p>{kitchen ? 'Update your kitchen information' : 'Set up your kitchen to start selling food'}</p>
          </div>

          <form onSubmit={kitchen ? handleUpdateKitchen : handleCreateKitchen}>
            {/* Basic Information */}
            <div className="user-profile-form-section">
              <h5>Basic Information</h5>
              <p className="section-description">Tell customers about your kitchen</p>
              
              <div className="user-profile-form-row">
                <div className="user-profile-form-group">
                  <label htmlFor="kitchen_name">Kitchen Name *</label>
                  <input
                    id="kitchen_name"
                    type="text"
                    value={formData.kitchen_name}
                    onChange={(e) => handleInputChange('kitchen_name', e.target.value)}
                    className={formErrors.kitchen_name ? 'error' : ''}
                    placeholder="e.g., Grandma's Kitchen"
                    maxLength={300}
                    required
                  />
                  {formErrors.kitchen_name && <div className="error-message">{formErrors.kitchen_name}</div>}
                </div>

                <div className="user-profile-form-group">
                  <label htmlFor="kitchen_type">Kitchen Type *</label>
                  <select
                    id="kitchen_type"
                    value={formData.kitchen_type}
                    onChange={(e) => handleInputChange('kitchen_type', e.target.value)}
                    className={formErrors.kitchen_type ? 'error' : ''}
                    required
                  >
                    <option value="">Select kitchen type</option>
                    {kitchenTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {formErrors.kitchen_type && <div className="error-message">{formErrors.kitchen_type}</div>}
                </div>
              </div>

              <div className="user-profile-form-row">
                <div className="user-profile-form-group">
                  <label htmlFor="contact_number">Contact Number *</label>
                  <input
                    id="contact_number"
                    type="tel"
                    value={formData.contact_number}
                    onChange={(e) => handleInputChange('contact_number', e.target.value)}
                    className={formErrors.contact_number ? 'error' : ''}
                    placeholder="Enter 10-digit number"
                    maxLength={10}
                    required
                  />
                  {formErrors.contact_number && <div className="error-message">{formErrors.contact_number}</div>}
                  <small style={{ color: '#666', fontSize: '0.8rem' }}>
                    Enter 10-digit mobile number without country code
                  </small>
                </div>

                <div className="user-profile-form-group">
                  <label htmlFor="kitchen_category">Kitchen Category *</label>
                  <select
                    id="kitchen_category"
                    value={formData.kitchen_category}
                    onChange={(e) => handleInputChange('kitchen_category', e.target.value)}
                    className={formErrors.kitchen_category ? 'error' : ''}
                    required
                  >
                    <option value="">Select category</option>
                    {kitchenCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                  {formErrors.kitchen_category && <div className="error-message">{formErrors.kitchen_category}</div>}
                </div>
              </div>

              <div className="user-profile-form-row">
                <div className="user-profile-form-group">
                  <label htmlFor="made_by">Chef/Owner Name *</label>
                  <input
                    id="made_by"
                    type="text"
                    value={formData.made_by}
                    onChange={(e) => handleInputChange('made_by', e.target.value)}
                    className={formErrors.made_by ? 'error' : ''}
                    placeholder="e.g., Chef Maria"
                    maxLength={100}
                    required
                  />
                  {formErrors.made_by && <div className="error-message">{formErrors.made_by}</div>}
                  <small style={{ color: '#666', fontSize: '0.8rem' }}>
                    Enter the name of the chef or owner
                  </small>
                </div>
              </div>
            </div>

            {/* Business Settings */}
            <div className="user-profile-form-section">
              <h5>Business Settings</h5>
              <p className="section-description">Configure delivery, pricing, and preparation settings for your kitchen.</p>

              <div className="user-profile-form-row">
                <div className="user-profile-form-group">
                  <label htmlFor="delivery_fee">Delivery Fee (£)</label>
                  <input
                    id="delivery_fee"
                    type="number"
                    min={0}
                    step={1}
                    value={formData.delivery_fee}
                    onChange={(e) => handleInputChange('delivery_fee', e.target.value)}
                    placeholder="e.g., 30"
                  />
                  <small style={{ color: '#666', fontSize: '0.8rem' }}>Base delivery fee charged to customers</small>
                </div>
                <div className="user-profile-form-group">
                  <label htmlFor="minimum_order_amount">Minimum Order (£)</label>
                  <input
                    id="minimum_order_amount"
                    type="number"
                    min={0}
                    step={1}
                    value={formData.minimum_order_amount}
                    onChange={(e) => handleInputChange('minimum_order_amount', e.target.value)}
                    placeholder="e.g., 100"
                  />
                  <small style={{ color: '#666', fontSize: '0.8rem' }}>Minimum cart value required to place an order</small>
                </div>
              </div>

              <div className="user-profile-form-row">
                <div className="user-profile-form-group">
                  <label htmlFor="average_preparation_time">Avg. Prep Time (minutes)</label>
                  <input
                    id="average_preparation_time"
                    type="number"
                    min={5}
                    step={5}
                    value={formData.average_preparation_time}
                    onChange={(e) => handleInputChange('average_preparation_time', e.target.value)}
                    placeholder="e.g., 30"
                  />
                  <small style={{ color: '#666', fontSize: '0.8rem' }}>Average time to prepare an order</small>
                </div>
                <div className="user-profile-form-group">
                  <label htmlFor="delivery_radius_km">Delivery Radius (km)</label>
                  <input
                    id="delivery_radius_km"
                    type="number"
                    min={1}
                    step={1}
                    value={formData.delivery_radius_km}
                    onChange={(e) => handleInputChange('delivery_radius_km', e.target.value)}
                    placeholder="e.g., 15"
                  />
                  <small style={{ color: '#666', fontSize: '0.8rem' }}>Maximum delivery distance from your kitchen</small>
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div className="user-profile-form-section">
              <div className="address-header">
                <h5>Location Details *</h5>
                <button
                  type="button"
                  onClick={handleDetectLocation}
                  disabled={isDetectingLocation}
                  className="detect-address-btn"
                >
                  {isDetectingLocation ? (
                    <>
                      <Loader size={16} className="user-profile-loading-spinner" />
                      Detecting...
                    </>
                  ) : (
                    <>
                      <MapPin size={16} />
                      Auto-Detect
                    </>
                  )}
                </button>
              </div>
              
              {locationMessage && (
                <div className={`location-status-message ${locationMessage.type}`}>
                  {locationMessage.type === 'success' && <CheckCircle size={16} />}
                  {locationMessage.type === 'error' && <AlertTriangle size={16} />}
                  {locationMessage.type === 'notice' && <MapPin size={16} />}
                  <span>{locationMessage.text}</span>
                </div>
              )}
              
              <div className="user-profile-form-group">
                <label htmlFor="kitchen_address">Kitchen Address *</label>
                <textarea
                  id="kitchen_address"
                  value={formData.kitchen_address}
                  onChange={(e) => handleInputChange('kitchen_address', e.target.value)}
                  className={formErrors.kitchen_address ? 'error' : ''}
                  placeholder="Enter your complete kitchen address including house number, street, area, city, and postal code..."
                  rows={3}
                  required
                />
                {formErrors.kitchen_address && <div className="error-message">{formErrors.kitchen_address}</div>}
                <small style={{ color: '#666', fontSize: '0.8rem' }}>
                  This address will be used for pickup and delivery coordination
                </small>
              </div>

              <div className="user-profile-form-row">
                <div className="user-profile-form-group">
                  <label htmlFor="city">City *</label>
                  <input
                    id="city"
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className={formErrors.city ? 'error' : ''}
                    placeholder="Enter city name"
                    required
                  />
                  {formErrors.city && <div className="error-message">{formErrors.city}</div>}
                </div>

                <div className="user-profile-form-group">
                  <label htmlFor="state">State *</label>
                  <input
                    id="state"
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    className={formErrors.state ? 'error' : ''}
                    placeholder="Enter state/province"
                    required
                  />
                  {formErrors.state && <div className="error-message">{formErrors.state}</div>}
                </div>
              </div>

              <div className="user-profile-form-row">
                <div className="user-profile-form-group">
                  <label htmlFor="country">Country *</label>
                  <select
                    id="country"
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    className={formErrors.country ? 'error' : ''}
                    required
                  >
                    <option value="">Select country</option>
                    {countries.map(country => (
                      <option key={country.code} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.country && <div className="error-message">{formErrors.country}</div>}
                </div>

                <div className="user-profile-form-group">
                  <label htmlFor="pincode">Pincode *</label>
                  <input
                    id="pincode"
                    type="text"
                    value={formData.pincode}
                    onChange={(e) => handleInputChange('pincode', e.target.value)}
                    className={formErrors.pincode ? 'error' : ''}
                    placeholder="Enter postal/zip code"
                    required
                  />
                  {formErrors.pincode && <div className="error-message">{formErrors.pincode}</div>}
                </div>
              </div>

              {/* Hidden coordinates — auto-filled by detect button */}
              <input type="hidden" value={formData.latitude} name="latitude" readOnly />
              <input type="hidden" value={formData.longitude} name="longitude" readOnly />
              <small style={{ color: '#666', fontSize: '0.8rem', marginTop: '-0.5rem', display: 'block' }}>
                Coordinates are detected automatically when you click Auto-Detect
              </small>
            </div>

            {/* Kitchen Image */}
            <div className="user-profile-form-section">
              <h5>Kitchen Photo {!kitchen && <span style={{ color: '#e53e3e' }}>*</span>}</h5>
              <p className="section-description">
                {kitchen ? 'Upload a new photo to replace the current one (optional)' : 'Upload a photo of your kitchen'}
              </p>

              <div className="user-profile-kitchen-image-upload">
                <input
                  type="file"
                  id="kitchen-image"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0])}
                  className="user-profile-hidden-input"
                />
                <label htmlFor="kitchen-image" className="user-profile-kitchen-upload-button">
                  <Upload size={20} />
                  {formData.image ? 'Change Photo' : kitchen ? 'Replace Photo' : 'Upload Photo'}
                </label>
                
                {imagePreview && (
                  <div className="user-profile-kitchen-image-preview">
                    <img src={imagePreview} alt="Kitchen preview" />
                  </div>
                )}
                
                {formErrors.image && <div className="error-message">{formErrors.image}</div>}
              </div>
            </div>

            {error && <div className="general-error">{error}</div>}

            {/* Form Actions */}
            <div className="user-profile-form-actions">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="user-profile-cancel-btn"
                disabled={isSubmitting}
              >
                <X size={16} />
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="user-profile-save-btn"
              >
                {isSubmitting ? (
                  <>
                    <div className="user-profile-loading-spinner" style={{ width: '16px', height: '16px' }}></div>
                    {kitchen ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {kitchen ? 'Update Kitchen' : 'Create Kitchen'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Food Listings Tab */}
      {activeTab === 'food_listings' && kitchen && (
        <div className="food-listings-container">
          <div className="food-listings-header">
            <div>
              <h4>My Food Listings</h4>
              <p>All food items you have listed on the platform</p>
            </div>
            <div className="food-listings-actions">
              <button
                className="user-profile-edit-btn"
                onClick={fetchFoodListings}
                disabled={foodListingsLoading}
              >
                <RefreshCw size={16} />
                Refresh
              </button>
              <button
                className="user-profile-save-btn"
                onClick={() => setShowSellFoodModal(true)}
                disabled={isKitchenBlocked}
                title={isKitchenBlocked ? 'Kitchen is blocked — cannot add food items' : 'Add a new food item'}
              >
                <PlusCircle size={16} />
                Add Food Item
              </button>
            </div>
          </div>

          {foodListingsLoading && (
            <div className="user-profile-empty-state">
              <div className="user-profile-loading-spinner" style={{ width: '40px', height: '40px', marginBottom: '1rem' }}></div>
              <p>Loading your food listings...</p>
            </div>
          )}

          {!foodListingsLoading && inventorySummary && (inventorySummary.low_stock_items > 0 || inventorySummary.sold_out_items > 0) && (
            <div className="inventory-alert-banner">
              <AlertTriangle size={16} />
              <span>
                {inventorySummary.sold_out_items > 0 && (
                  <strong>{inventorySummary.sold_out_items} sold out</strong>
                )}
                {inventorySummary.sold_out_items > 0 && inventorySummary.low_stock_items > 0 && ' · '}
                {inventorySummary.low_stock_items > 0 && (
                  <strong>{inventorySummary.low_stock_items} low stock</strong>
                )}
                {' '}— update quantities below to keep your menu accurate.
              </span>
            </div>
          )}

          {foodListingsError && !foodListingsLoading && (
            <div className="user-profile-empty-state">
              <AlertTriangle size={48} />
              <p>{foodListingsError}</p>
              <button className="user-profile-edit-btn" onClick={fetchFoodListings} style={{ marginTop: '1rem' }}>
                <RefreshCw size={16} />
                Try Again
              </button>
            </div>
          )}

          {!foodListingsLoading && !foodListingsError && foodListings.length === 0 && (
            <div className="user-profile-empty-state">
              <UtensilsCrossed size={60} />
              <p>You haven't listed any food items yet.</p>
              <button className="user-profile-save-btn" onClick={() => setShowSellFoodModal(true)} style={{ marginTop: '1rem' }} disabled={isKitchenBlocked}>
                <PlusCircle size={16} />
                Add Your First Food Item
              </button>
            </div>
          )}

          {!foodListingsLoading && foodListings.length > 0 && (
            <div className="food-listings-grid">
              {foodListings.map((item) => {
                const isLowStock = item.available_quantity <= item.low_stock_threshold;
                return (
                  <div key={item._id || item.food_id} className="food-listing-card">
                    <div className="food-listing-image-wrap">
                      <img
                        src={getFirstImageUrl(item.image_urls)}
                        alt={item.food_name}
                        className="food-listing-image"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = FOOD_FALLBACK_IMG;
                        }}
                      />
                      <span className={`food-listing-avail-badge ${item.is_available ? 'avail--on' : 'avail--off'}`}>
                        {item.is_available ? 'Available' : 'Unavailable'}
                      </span>
                      {getAllImageUrls(item.image_urls).length > 1 && (
                        <span className="food-listing-img-count">
                          {getAllImageUrls(item.image_urls).length} photos
                        </span>
                      )}
                    </div>

                    <div className="food-listing-body">
                      <div className="food-listing-title-row">
                        <h5 className="food-listing-name">{item.food_name}</h5>
                        <span className={`food-listing-category ${item.category?.toLowerCase().includes('veg') && !item.category?.toLowerCase().includes('non') ? 'cat--veg' : 'cat--nonveg'}`}>
                          {item.category}
                        </span>
                      </div>

                      <p className="food-listing-desc">{item.description}</p>

                      <div className="food-listing-meta">
                        <div className="fl-meta-item">
                          <Clock size={13} />
                          <span>{item.preparation_time}</span>
                        </div>
                        <div className="fl-meta-item">
                          <User size={13} />
                          <span>{item.serving_size}</span>
                        </div>
                        <div className="fl-meta-item">
                          <MapPin size={13} />
                          <span>{item.pickup_location && item.pickup_location.length > 20 ? `${item.pickup_location.substring(0, 20)}...` : item.pickup_location}</span>
                        </div>
                      </div>

                      <div className="food-listing-footer">
                        <span className="food-listing-price">£{item.price}</span>
                        <div className="food-listing-qty-info">
                          <Package size={13} />
                          <span className={isLowStock ? 'qty--low' : ''}>
                            {item.available_quantity} left
                            {isLowStock && ' (Low stock)'}
                          </span>
                        </div>
                      </div>

                      {/* Restock inline panel */}
                      {restockFoodId === item.food_id ? (
                        <div className="fl-restock-row">
                          <input
                            type="number"
                            min="1"
                            className="inventory-qty-input"
                            placeholder="Units to add"
                            value={restockQty}
                            onChange={e => setRestockQty(e.target.value)}
                            autoFocus
                          />
                          <button
                            className="inventory-qty-save-btn"
                            disabled={isRestocking}
                            onClick={() => handleRestock(item.food_id)}
                          >
                            {isRestocking ? <div className="user-profile-loading-spinner" style={{ width: '14px', height: '14px' }} /> : <Save size={14} />}
                            Add
                          </button>
                          <button
                            className="fl-action-btn fl-action-btn--ghost"
                            onClick={() => { setRestockFoodId(null); setRestockQty(''); }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="inventory-qty-editor">
                          <label className="inventory-qty-label">Set stock</label>
                          <div className="inventory-qty-row">
                            <input
                              type="number"
                              min="0"
                              className="inventory-qty-input"
                              value={inventoryEdits[item.food_id] ?? String(item.available_quantity)}
                              onChange={(e) =>
                                setInventoryEdits(prev => ({ ...prev, [item.food_id]: e.target.value }))
                              }
                            />
                            <button
                              className="inventory-qty-save-btn"
                              disabled={savingFoodId === item.food_id}
                              onClick={() => handleSaveQuantity(item.food_id)}
                            >
                              {savingFoodId === item.food_id ? (
                                <div className="user-profile-loading-spinner" style={{ width: '14px', height: '14px' }}></div>
                              ) : (
                                <Save size={14} />
                              )}
                              Save
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Action buttons row */}
                      <div className="fl-actions-row">
                        <button
                          className="fl-action-btn fl-action-btn--edit"
                          onClick={() => setEditingFood(item)}
                          title="Edit"
                          disabled={isKitchenBlocked}
                        >
                          <Edit size={14} />
                          Edit
                        </button>
                        <button
                          className="fl-action-btn fl-action-btn--restock"
                          onClick={() => { setRestockFoodId(item.food_id); setRestockQty(''); }}
                          title="Restock"
                          disabled={isKitchenBlocked}
                        >
                          <PackagePlus size={14} />
                          Restock
                        </button>
                        <button
                          className={`fl-action-btn ${item.is_available ? 'fl-action-btn--disable' : 'fl-action-btn--enable'}`}
                          disabled={isKitchenBlocked || togglingFoodId === item.food_id}
                          onClick={() => handleToggleStatus(item.food_id)}
                          title={item.is_available ? 'Mark Unavailable' : 'Mark Available'}
                        >
                          {togglingFoodId === item.food_id
                            ? <div className="user-profile-loading-spinner" style={{ width: '12px', height: '12px' }} />
                            : <Power size={14} />}
                          {item.is_available ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          className="fl-action-btn fl-action-btn--delete"
                          onClick={() => setDeletingFoodId(item.food_id)}
                          title="Delete"
                          disabled={isKitchenBlocked}
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showSellFoodModal && (
        <SellFoodModal onClose={() => { setShowSellFoodModal(false); fetchFoodListings(); }} />
      )}

      {editingFood && (
        <EditFoodModal
          food={editingFood}
          onClose={() => setEditingFood(null)}
          onSuccess={fetchFoodListings}
          showNotification={showNotification}
        />
      )}

      {deletingFoodId !== null && (
        <div className="efm-overlay" onClick={() => setDeletingFoodId(null)}>
          <div className="efm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '380px' }}>
            <h3 className="efm-title">Delete Food Item?</h3>
            <p style={{ color: '#6b7280', margin: '0.5rem 0 1.5rem', fontSize: '0.9rem' }}>
              This action cannot be undone. The food item will be permanently removed from the platform.
            </p>
            <div className="efm-actions">
              <button className="efm-btn efm-btn--cancel" onClick={() => setDeletingFoodId(null)} disabled={isDeletingFood}>
                Cancel
              </button>
              <button
                className="efm-btn"
                style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#fff' }}
                onClick={() => handleDeleteFood(deletingFoodId!)}
                disabled={isDeletingFood}
              >
                {isDeletingFood ? <><span className="efm-spinner" />Deleting...</> : <><Trash2 size={16} />Delete</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kitchen Dashboard */}
      {activeTab === 'dashboard' && kitchen && (
        <KitchenDashboardTab showNotification={showNotification} isKitchenBlocked={isKitchenBlocked} />
      )}

      {/* No Kitchen State */}
      {!kitchen && activeTab === 'view' && !isLoading && (
        <div className="user-profile-empty-state">
          <ChefHat size={60} />
          <p>You haven't created a kitchen yet. Create one to start selling your delicious food!</p>
          <button 
            onClick={() => setActiveTab('create')}
            className="user-profile-create-kitchen-btn"
          >
            <ChefHat size={20} />
            Create Kitchen
          </button>
        </div>
      )}

      {/* Delete Kitchen Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="user-profile-confirm-dialog-overlay">
          <div className="user-profile-delete-modal">
            <div className="user-profile-delete-modal-header">
              <div className="user-profile-delete-modal-icon">
                <AlertTriangle size={24} />
              </div>
              <h3 className="user-profile-delete-modal-title">Delete Kitchen</h3>
            </div>
            
            <div className="user-profile-delete-modal-content">
              <p className="user-profile-delete-modal-text">
                Are you sure you want to delete your kitchen? This action cannot be undone.
              </p>
              
              <div className="user-profile-delete-consequences-list">
                <h4 className="user-profile-delete-consequences-title">What will be deleted:</h4>
                <div className="user-profile-delete-consequences-items">
                  <div className="user-profile-delete-consequence-item">Kitchen information</div>
                  <div className="user-profile-delete-consequence-item">All food listings</div>
                  <div className="user-profile-delete-consequence-item">Order history</div>
                  <div className="user-profile-delete-consequence-item">Customer reviews</div>
                </div>
              </div>
              
              <div className="user-profile-delete-final-warning">
                <p className="user-profile-delete-final-warning-text">
                  ⚠️ This action is permanent and cannot be undone
                </p>
              </div>
            </div>
            
            <div className="user-profile-delete-modal-actions">
              <button 
                onClick={() => setShowDeleteConfirmation(false)}
                className="user-profile-delete-cancel-btn"
                disabled={isDeletingKitchen}
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteKitchen}
                disabled={isDeletingKitchen}
                className="user-profile-delete-confirm-btn"
              >
                {isDeletingKitchen ? (
                  <>
                    <div className="user-profile-delete-spinner"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete Forever
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserKitchenManager;